import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, PlayIcon } from 'lucide-react';
import type { SlideProps } from './SlideShell';
import { FerringWordmark } from '../FerringWordmark';
import { LivePrototype } from '../LivePrototype';
const EASE = [0.23, 1, 0.32, 1] as const;
export function SlideOpening({
  onNavigate
}: SlideProps) {
  const [playKey, setPlayKey] = useState(0);
  const secondary = 'inline-flex items-center gap-2 rounded-full border border-[var(--fg)]/15 bg-white px-[clamp(1rem,1.5vw,1.35rem)] py-[clamp(0.55rem,0.9vw,0.8rem)] text-[clamp(0.68rem,0.85vw,0.8rem)] font-bold uppercase tracking-[0.14em] text-[var(--fg)] transition-[border-color,transform] duration-150 hover:border-[var(--fg)]/40 active:scale-[0.97] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)] focus-visible:ring-offset-2';
  return <div className="relative flex h-full w-full overflow-hidden bg-[var(--surface)]" data-id="element-478" data-mp="2jgirz6ivaev1">
      <motion.div initial={{
      scaleX: 0
    }} animate={{
      scaleX: 1
    }} transition={{
      duration: 0.8,
      ease: 'easeOut'
    }} className="absolute left-0 top-0 h-1 w-full origin-left bg-[var(--brand)]" data-id="element-479" data-mp="2jiwjj5ec98xu" />

      <div className="flex min-h-0 flex-1 items-stretch gap-[clamp(2rem,5vw,5rem)] px-[clamp(2rem,5vw,5rem)] pb-[clamp(3.5rem,5vw,4.5rem)] pt-[clamp(3.5rem,5.5vw,5rem)]" data-id="element-480" data-mp="2twdf1mr9m9bm">
        <div className="flex min-w-0 flex-[1.05] flex-col justify-center" data-id="element-481" data-mp="3mkj42lpcqkh9">
          <motion.div initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1,
          duration: 0.5
        }} className="flex items-center gap-4" data-id="element-482" data-mp="3mlc4z61mqfrh">
            <FerringWordmark data-id="element-483" data-mp="3dit2in1ra55r" />
            <span className="h-7 w-px bg-[var(--fg)]/15" aria-hidden="true" data-id="element-484" data-mp="3djkza173a8xm" />
            <p className="text-[clamp(0.62rem,0.85vw,0.8rem)] font-bold uppercase tracking-[0.24em] text-[var(--fg)]" data-id="element-485" data-mp="3hk4rcqct2mcw">
              REKOVELLE × Snakker<sup className="text-[0.6em]" data-id="element-486" data-mp="2271z2iezrzpd">™</sup>
            </p>
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          y: 22
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2,
          duration: 0.7,
          ease: EASE
        }} className="mt-[clamp(1.5rem,3vw,2.75rem)] text-[clamp(2.4rem,5.4vw,5.4rem)] font-extrabold leading-[0.96] tracking-[-0.055em] text-[var(--fg)]" data-id="element-487" data-mp="2646fkeq8tm7z">
            Make the benefit something the HCP can <span className="text-[var(--brand)]" data-id="element-488" data-mp="3u3s0zfgo8qpf">experience.</span>
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 14
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.45,
          duration: 0.55
        }} className="mt-[clamp(1rem,2vw,1.6rem)] max-w-xl text-[clamp(0.95rem,1.35vw,1.25rem)] font-medium leading-snug text-[var(--muted)]" data-id="element-489" data-mp="3q41evp23ayr8">
            A short interactive example of how a complex product strength can become easier to engage with, understand and remember.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 14
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.6,
          duration: 0.5
        }} className="mt-[clamp(1.5rem,2.8vw,2.5rem)] flex flex-wrap items-center gap-[clamp(0.5rem,0.8vw,0.75rem)] hide-on-print" data-id="element-490" data-mp="19dkvclc2riw7">
            <button type="button" onClick={() => setPlayKey(k => k + 1)} className="inline-flex items-center gap-2 rounded-full bg-[var(--brand)] px-[clamp(1.1rem,1.7vw,1.5rem)] py-[clamp(0.6rem,0.95vw,0.85rem)] text-[clamp(0.68rem,0.85vw,0.8rem)] font-bold uppercase tracking-[0.14em] text-white shadow-sm transition-[background-color,transform] duration-150 hover:bg-[var(--fg)] active:scale-[0.97] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)] focus-visible:ring-offset-2" data-id="element-491" data-mp="1qo6kw8x0ojbg">
              <PlayIcon className="h-3.5 w-3.5 fill-current" aria-hidden="true" data-id="element-492" data-mp="2jc2qk34kdlkd" />
              Play the Snak
            </button>
            {onNavigate && <>
                <button type="button" onClick={() => onNavigate('model')} className={secondary} data-id="element-493" data-mp="2ocqg0kd59uch">
                  The Snakker idea
                  <ArrowRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-494" data-mp="3s9am8a4un3zd" />
                </button>
                <button type="button" onClick={() => onNavigate('signals')} className={secondary} data-id="element-495" data-mp="a3ny15dhxxjc">
                  What we learn
                  <ArrowRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-496" data-mp="52i1sxkmns52" />
                </button>
              </>}
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 24
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.15,
        duration: 0.65,
        ease: EASE
      }} className="hidden min-w-0 flex-1 md:flex" data-id="element-497" data-mp="f30t2deiuax0">
          <LivePrototype playKey={playKey} data-id="element-498" data-mp="oskbxj28i4rv" />
        </motion.div>
      </div>

      <p className="absolute bottom-4 left-[clamp(2rem,5vw,5rem)] font-mono text-[clamp(0.55rem,0.7vw,0.66rem)] tracking-[0.14em] text-[var(--muted)]" data-id="element-499" data-mp="12sv5ituxhxgq">
        Illustrative concept · not a final campaign recommendation
      </p>
    </div>;
}